// M3 offshore operations: shared helpers (M3_SPEC §1–§6). Pure lookups + tiny state accessors; no top-level use of
// imported sim bindings (circular-import safe). Everything is gated at yf(state) >= M3_FROM by the callers.
import { yf, yearOf, eqDef, fieldDef, stat } from '../defs.js';
import { inflate } from '../../data/cpi.js';
import { M3_FROM, OFFSHORE, LOGISTICS, dayRate } from '../../data/m3/rules.js';
import { plotWaterDepth, fieldMaxWater, seasonFor } from '../../data/m3/fields.js';
import { maxWaterFor } from '../../data/m3/equipment.js';
import { dayToDate } from '../../core/time.js';
import { notify } from '../util.js';
import { RUSH_K } from '../timers.js';
import { m4DayRate, m4UtilizationFor } from '../m4/marketRigs.js'; // M4-B: DAY_RATES_M4 & rig/floater utilization from 2000

export { plotWaterDepth, fieldMaxWater, seasonFor, maxWaterFor };

export const m3On = (state) => yf(state) >= M3_FROM;

// Lazy M3 ops state (never created before 1946, so M1/M2 saves stay byte-identical).
export function m3(state) {
  return (state.m3 ??= { charterBoard: [], boardDay: 0, eor: {}, megaprojects: {}, supply: {}, crewChange: {}, stormsSeen: {}, nextStorm: 1 });
}
export const platforms = (state) => (state.platforms ??= {});
export const logisticsState = (state) => (state.logistics ??= {});

export function plotWater(fieldId, idx) {
  const f = fieldDef(fieldId);
  return f ? Math.max(0, plotWaterDepth(f, idx) ?? 0) : 0;
}

// A rig's build year (envelope). Bought from 1946 on: stamped at purchase; older rigs: their def's year.
export function rigBuiltYear(rig) {
  if (rig?.builtYear) return rig.builtYear;
  return eqDef(rig?.defId)?.year ?? 1859;
}

export function envelope(def, builtYear) {
  const s = def?.stats ?? {};
  return {
    offshore: !!s.offshore, minWater: s.minWater ?? 0, maxWater: s.offshore ? maxWaterFor(def, builtYear) : 0, maxDepth: s.maxDepth ?? 0,
    stationKeeping: s.stationKeeping ?? (s.offshore ? 'bottom' : 'land'), rigClass: s.rigClass ?? null, bopType: s.bopType ?? 'surface',
    floating: !!s.floating, opLimitHsFt: s.opLimitHsFt ?? (s.offshore ? 15 : 99), survivalHsFt: s.survivalHsFt ?? (s.offshore ? 40 : 99),
    needsPlatform: !!s.needsPlatform, coldRated: !!s.coldRated, pob: s.pob ?? 0, deckStorageDays: s.deckStorageDays ?? 20,
  };
}

// Why a rig (def, build year) cannot drill `tdBml` ft below the mudline in `water` ft: null = it fits.
export function envelopeProblem(def, builtYear, water, tdBml = 0) {
  const e = envelope(def, builtYear);
  const name = def?.name ?? 'rig';
  if (!e.offshore) {
    if (water > 0) return `${name} is a land rig — this plot is under ${Math.round(water)} ft of water.`;
    return null;
  }
  if (water <= 0) return `${name} is a marine rig — this plot is on dry land.`;
  if (water < e.minWater) return `Too shallow for a ${Math.round(builtYear)} ${e.rigClass ?? 'rig'} (needs ${e.minWater} ft of water, plot has ${Math.round(water)} ft).`;
  if (water > e.maxWater) return `Too deep for a ${Math.round(builtYear)}-built ${e.rigClass ?? 'rig'} (rated to ${e.maxWater} ft, plot has ${Math.round(water)} ft).`;
  if (e.maxDepth < water + tdBml) return `${name} reaches ${e.maxDepth.toLocaleString('en-US')} ft from the rig floor — not ${Math.round(water + tdBml).toLocaleString('en-US')} ft (water + hole).`;
  return null;
}

export const month = (state) => dayToDate(state.day).month;

// Logistics day rate (era $): stats.dayRate is in the def's base-year dollars.
export function serviceRate(state, def) {
  const r = stat(def, 'dayRate', 0);
  return r > 0 ? inflate(r, def.baseYear ?? def.year ?? 1950, Math.min(yf(state), 2025)) : 0;
}

// Charter day rate (nominal $/day) for a rig def at the current date, scaled by fleet utilization (0.9–1.2).
export function charterRate(state, def, util = 0.7) {
  const cls = stat(def, 'dayRateClass', stat(def, 'offshore', false) ? 'jackup' : 'land');
  const base = m4DayRate(cls, yf(state)) ?? dayRate(cls, yf(state)); // M4-B: M4 curves from 2000 (null before)
  util = m4UtilizationFor(def, yf(state)) ?? util; // M4-B
  return base * (0.9 + 0.3 * Math.max(0, Math.min(1, (util - 0.45) / 0.53)));
}

// Great-circle distance between two fields (nm); 0 if either has no coordinates.
export function fieldNm(aId, bId) {
  const a = fieldDef(aId), b = fieldDef(bId);
  if (!a || !b || a.lat == null || b.lat == null || aId === bId) return 0;
  const R = 3440, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function problem(state, well, type, text, { pause = false } = {}) {
  if (well.problem?.type === type) return;
  well.problem = { type, sinceDay: state.day, text };
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, text: `${well.name}: ${text}`, problem: type, pause, level: pause ? 'warn' : 'info' });
}

export function ensureRushKinds() {
  RUSH_K.PLATFORM_BUILD ??= 0.5;
  RUSH_K.PLATFORM_REPAIR ??= 1.0;
}

// Field-level environment helpers
export const envOf = (fieldId) => fieldDef(fieldId)?.environment ?? null;
export const distanceToShore = (fieldId) => fieldDef(fieldId)?.distanceToShoreMi ?? 30;
export const crewBoatOk = (fieldId) => distanceToShore(fieldId) <= LOGISTICS.crewBoatMaxMi;
export const offshoreOpexMult = () => OFFSHORE.opexMult;
export const yearNow = (state) => yearOf(state);
